import React, { createContext, useContext, useState, useCallback } from 'react';

const PlayerContext = createContext(null);

const SESSION_KEY = 'golf_session';

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSession(data) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {}
}

function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {}
}

function PlayerProvider({ children }) {
  const saved = loadSession();
  const [playerId, _setPlayerId] = useState(saved?.playerId || null);
  const [displayName, _setDisplayName] = useState(saved?.displayName || '');
  const [isHost, _setIsHost] = useState(saved?.isHost || false);
  const [drawnCard, setDrawnCard] = useState(null);
  const [drawnFrom, setDrawnFrom] = useState(null);

  const persist = useCallback((updates) => {
    const current = loadSession() || {};
    saveSession({ ...current, ...updates });
  }, []);

  const setPlayerId = useCallback((id) => {
    _setPlayerId(id);
    persist({ playerId: id });
  }, [persist]);

  const setDisplayName = useCallback((name) => {
    _setDisplayName(name);
    persist({ displayName: name });
  }, [persist]);

  const setIsHost = useCallback((host) => {
    _setIsHost(host);
    persist({ isHost: host });
  }, [persist]);

  const resetPlayer = useCallback(() => {
    _setPlayerId(null);
    _setDisplayName('');
    _setIsHost(false);
    setDrawnCard(null);
    setDrawnFrom(null);
    clearSession();
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        playerId,
        setPlayerId,
        displayName,
        setDisplayName,
        isHost,
        setIsHost,
        drawnCard,
        setDrawnCard,
        drawnFrom,
        setDrawnFrom,
        resetPlayer,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}

export { PlayerProvider, usePlayer };
